/**
 * Core vault types shared by server, web client, and index worker.
 *
 * Identity model: every vault file has a stable `id` (nanoid). The vault
 * metadata Y.Doc (doc key "meta") holds a Y.Map named "files" mapping
 * id -> VaultFileEntry (plain JSON object, replaced wholesale on change).
 * Notes additionally have their own Y.Doc (doc key `note:<id>`) containing
 * a Y.Text named "content" — the full markdown text including frontmatter.
 * Renames only change `path` in the meta entry; the id (and thus doc key,
 * history, links index) is stable.
 */

/** Kind of vault file. `note` = .md with a Y.Doc; `canvas` = .canvas JSON with
 * a Y.Doc (content is the JSON text); `file` = binary/attachment, no Y.Doc. */
export type FileKind = 'note' | 'canvas' | 'file';

export interface VaultFileEntry {
  /** Vault-relative POSIX path, e.g. "Projects/Foo.md". Never starts with "/". */
  path: string;
  kind: FileKind;
  /** Creation time, epoch ms (used by graph timelapse). */
  ctime: number;
  /** Last modification time, epoch ms. */
  mtime: number;
  /** Byte size (attachments only; informational). */
  size?: number;
  /** Tombstone: epoch ms when deleted. Deleted entries stay in the map. */
  deleted?: number;
}

/** Doc keys on the sync wire and in persistence. */
export const META_DOC_KEY = 'meta';
export const noteDocKey = (id: string) => `note:${id}`;
export const isNoteDocKey = (key: string) => key.startsWith('note:');
export const noteIdFromDocKey = (key: string) => key.slice('note:'.length);

/** Name of the Y.Map inside the meta doc. */
export const META_FILES_MAP = 'files';
/** Name of the Y.Map inside the meta doc for vault-wide settings (daily note
 * folder, templates folder, attachment folder, graph color groups...). */
export const META_SETTINGS_MAP = 'settings';
/** Name of the Y.Text inside each note doc. */
export const NOTE_TEXT = 'content';

// ---------------------------------------------------------------------------
// Markdown parse results (produced by packages/shared parser, consumed by the
// index worker, editor decorations, and server link rewriting)
// ---------------------------------------------------------------------------

export interface WikiLink {
  /** Raw source including brackets (and leading ! for embeds). */
  raw: string;
  /** Target note portion: path or bare name; "" for same-note refs like [[#h]]. */
  target: string;
  /** Heading anchor without '#'; nested subheads joined with '#'. */
  heading?: string;
  /** Block anchor without '^'. */
  blockId?: string;
  /** Display alias after '|'. */
  alias?: string;
  embed: boolean;
  /** Absolute char offsets in the markdown source. */
  start: number;
  end: number;
}

export interface MdLink {
  href: string;
  text: string;
  /** True when href points inside the vault (relative, no scheme). */
  internal: boolean;
  embed: boolean;
  start: number;
  end: number;
}

export interface TagRef {
  /** Without '#', nesting preserved: "project/active". */
  tag: string;
  start: number;
  end: number;
}

export interface HeadingRef {
  level: number;
  /** Raw heading text (markdown formatting kept). */
  text: string;
  /** Anchor form used for [[note#anchor]] matching: formatting stripped, trimmed. */
  anchor: string;
  start: number;
  end: number;
}

export interface BlockRef {
  /** Block id without '^'. */
  id: string;
  start: number;
  end: number;
}

export interface NoteParse {
  /** Parsed YAML frontmatter, or null when absent/invalid. */
  frontmatter: Record<string, unknown> | null;
  /** Offset where the body (after frontmatter) begins. 0 when no frontmatter. */
  bodyStart: number;
  /** Aliases from frontmatter (alias/aliases keys). */
  aliases: string[];
  /** Tags contributed by frontmatter (tag/tags keys), no '#'. */
  fmTags: string[];
  wikilinks: WikiLink[];
  mdLinks: MdLink[];
  /** Inline #tags from the body. */
  tags: TagRef[];
  headings: HeadingRef[];
  blocks: BlockRef[];
}

// ---------------------------------------------------------------------------
// Vault settings (stored in meta doc "settings" map, all optional)
// ---------------------------------------------------------------------------

export interface VaultSettings {
  /** Folder for daily notes, default "Daily". */
  dailyFolder?: string;
  /** dayjs format for daily note names, default "YYYY-MM-DD". */
  dailyFormat?: string;
  /** Template file path applied to new daily notes. */
  dailyTemplate?: string;
  /** Folder containing templates, default "Templates". */
  templatesFolder?: string;
  /** Folder for pasted/uploaded attachments, default "attachments". */
  attachmentFolder?: string;
  /** Graph color groups: query -> color. Evaluated in order. */
  graphGroups?: { query: string; color: string }[];
}

// ---------------------------------------------------------------------------
// REST API shapes
// ---------------------------------------------------------------------------

export interface AuthStatus {
  needsSetup: boolean;
  authenticated: boolean;
  username?: string;
}

export interface HistoryEntry {
  hash: string;
  date: string; // ISO
  message: string;
}

export interface HistoryDiff {
  hash: string;
  content: string;
}
